import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowClockwise, MicrosoftExcelLogo } from "@phosphor-icons/react";
import PageHeader from "@/components/PageHeader";
import { api, formatApiError } from "@/lib/api";
import { GateRegisterTable, TABS } from "@/components/gate/GateRegisterTable";

const today = () => new Date().toISOString().slice(0, 10);
const monthStart = () => `${today().slice(0, 8)}01`;
const FIELD = "border border-canvas-line bg-white px-3 py-2 text-sm focus:outline-none focus:border-ink";

function Kpis({ s }) {
  const items = [
    ["Customer passes", s.customer_passes], ["Exited", s.customer_exited], ["Not exited yet", s.customer_not_exited],
    ["Test rides", s.test_rides], ["Out now", s.test_rides_out_now], [`Late (>${s.max_minutes} min)`, s.test_rides_late, s.test_rides_late > 0],
    ["Test ride km", s.test_ride_km], ["Avg ride min", s.avg_ride_minutes], ["Vehicles in / out", `${s.vehicles_in} / ${s.vehicles_out}`],
  ];
  return (
    <div className="grid grid-cols-3 lg:grid-cols-9 gap-2" data-testid="gr-kpis">
      {items.map(([l, v, warn]) => (
        <div key={l} className={`border px-3 py-2 ${warn ? "border-[#b91c1c] bg-[#fde8e8]" : "border-canvas-line bg-white"}`}>
          <p className="font-mono text-[9px] uppercase tracking-[0.15em] text-ink-muted">{l}</p>
          <p className="font-bold text-base">{v}</p>
        </div>
      ))}
    </div>
  );
}

export default function GateRegisterReportPage() {
  const [f, setF] = useState({ date_from: monthStart(), date_to: today(), branch_id: "" });
  const [branches, setBranches] = useState([]);
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("customer_exits");
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const params = () => Object.fromEntries(Object.entries(f).filter(([, v]) => v));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/reports/gate-register", { params: params() });
      setData(d);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [f]); // eslint-disable-line react-hooks/exhaustive-deps

  const exportXlsx = async () => {
    setExporting(true);
    try {
      const r = await api.get("/reports/gate-register/export.xlsx", { params: params(), responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(r.data);
      a.download = `gate-register_${f.date_from}_${f.date_to}.xlsx`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { api.get("/branches").then((r) => setBranches(r.data || [])).catch(() => {}); }, []);

  return (
    <div data-testid="gate-register-page">
      <PageHeader eyebrow="Service / Reports" title="Gate Register" subtitle="Customer exits, technician test rides and other vehicles at the gate — with photos and km." />
      <div className="px-8 py-6 space-y-5">
        <div className="flex items-end gap-2 flex-wrap">
          <input type="date" value={f.date_from} onChange={set("date_from")} className={FIELD} data-testid="gr-from" />
          <input type="date" value={f.date_to} onChange={set("date_to")} className={FIELD} data-testid="gr-to" />
          <select value={f.branch_id} onChange={set("branch_id")} className={FIELD} data-testid="gr-branch">
            <option value="">All branches</option>
            {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
          <button onClick={load} disabled={loading} className="inline-flex items-center gap-1.5 px-4 py-2 bg-ink text-white text-sm font-semibold disabled:opacity-50" data-testid="gr-apply">
            <ArrowClockwise size={14} className={loading ? "animate-spin" : ""} /> Apply
          </button>
          <button onClick={exportXlsx} disabled={exporting || !data} data-testid="gr-export"
            className="inline-flex items-center gap-1.5 px-4 py-2 border border-[#047857] text-[#047857] text-sm font-semibold disabled:opacity-40">
            <MicrosoftExcelLogo size={14} /> {exporting ? "Building Excel…" : "Export Excel (with photos)"}
          </button>
        </div>
        {data && (
          <>
            <Kpis s={data.summary} />
            <div className="inline-flex border border-ink" data-testid="gr-tabs">
              {TABS.map(([k, l]) => (
                <button key={k} type="button" onClick={() => setTab(k)} data-testid={`gr-tab-${k}`}
                  className={`px-4 py-2 text-sm font-semibold ${tab === k ? "bg-ink text-white" : "bg-white"}`}>
                  {l} ({data[k].length})
                </button>
              ))}
            </div>
            <GateRegisterTable tab={tab} rows={data[tab]} />
          </>
        )}
      </div>
    </div>
  );
}
